import type { HoverParam } from '@/components/MiniPriceChart';
import type { FragmentOf } from '@/utilities/gql';
import { useCallback, useState } from 'react';
import type { NARRATIVE_COMPANY_PRIMARY_VALUATION_FRAGMENT } from '../narrative-company-fragment';

type Props = {
  company: FragmentOf<
    typeof NARRATIVE_COMPANY_PRIMARY_VALUATION_FRAGMENT
  > | null;
  defaultLastSharePrice?: number | null;
};

export const useNarrativeChartSelection = ({
  company,
  defaultLastSharePrice,
}: Props) => {
  const [selectedTime, setSelectedTime] = useState<number | null>(null);
  const [selectedPrice, setSelectedPrice] = useState<number | null>(null);
  const [selectedFairValue, setSelectedFairValue] = useState<number | null>(
    null
  );

  const onHover = useCallback(({ time, value, fairValue }: HoverParam) => {
    setSelectedTime(time);
    setSelectedPrice(value);
    setSelectedFairValue(fairValue);
  }, []);

  if (!company) {
    return {
      date: null,
      lastSharePrice: null,
      fairValue: null,
      valuations: null,
      onHover: () => {},
    };
  }

  const { myValuation, defaultNarrative, analysisValue } = company;

  const fairValue =
    myValuation?.fairValue ?? defaultNarrative?.valuation?.fairValue;
  const valuations =
    myValuation?.narrative?.valuations ?? defaultNarrative?.valuations;
  const updatedAt =
    myValuation?.narrative?.updatedAt ?? defaultNarrative?.updatedAt;
  const lastSharePrice =
    typeof defaultLastSharePrice === 'undefined'
      ? analysisValue?.lastSharePrice
      : defaultLastSharePrice;

  const getIsoDate = () => {
    if (selectedTime) {
      return new Date(selectedTime).toISOString();
    }
    if (updatedAt) {
      return updatedAt;
    }
    return null;
  };

  return {
    date: getIsoDate(),
    lastSharePrice: selectedPrice ?? lastSharePrice ?? null,
    fairValue: selectedFairValue ?? fairValue ?? null,
    valuations,
    onHover,
  };
};
